import DailyAttendanceReport from './DailyAttendanceReport';
import useParityConfirm from '../common/useParityConfirm';
import React, { useCallback, useEffect, useRef, useState } from "react";
import MainTitle from "../common/MainTitle";
import Table from "../common/Table";
import RowPhoto from "../common/TdImg";
import AccessDeniedResponse from "../common/AccessDeniedResponse";
import {
  ActionGroup,
  EmptyState,
  FilterBar,
  LoadingState,
  SectionCard,
  StatusBadge,
} from "../common/SurfaceUI";
import db from "../../services/query";
import "./attendanceAdmin.css";
import ParityReport from '../common/ParityReport';

export const errorMessage = (err) => err?.response?.data?.error?.message || err?.message || "عملیات ناموفق بود. دوباره کوشش کنید.";
const stateLabel = (n) => n == null ? "ثبت نشده" : ({ 1: "حاضر", 2: "غیر حاضر", 3: "جایگزین شده", 4: "رخصت" }[n] || String(n));
const confirmationLabel = (n) => n == null ? "در انتظار" : Number(n) === 1 ? "تایید" : "رد";
const clockTime = (value) => value ? String(value).slice(-8) : "";
const stateTone = (n) => n == null ? "neutral" : ({ 1: "success", 2: "danger", 3: "info", 4: "warning" }[Number(n)] || "neutral");
const confirmationTone = (n) => n == null ? "warning" : Number(n) === 1 ? "success" : "danger";

export default function AttendanceAdmin() {
  const [confirm, confirmationDialog] = useParityConfirm();
  const [kind, setKind] = useState("teachers");
  const [date, setDate] = useState("");
  const [dateReady, setDateReady] = useState(false);
  const [todayIsHoliday, setTodayIsHoliday] = useState(false);
  const [mode, setMode] = useState("all");
  const [search,setSearch]=useState('');
  const [adminState,setAdminState]=useState(''),[headState,setHeadState]=useState('');
  const extraFilters=new URLSearchParams({search});if(adminState)extraFilters.set('adminState',adminState);if(headState)extraFilters.set('headState',headState);
  const [page, setPage] = useState(1);
  const [body, setBody] = useState({ data: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [message, setMessage] = useState("");
  const [editor, setEditor] = useState(null);
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const generation = useRef(0);
  const caps = body.summary?.capabilities || {};
  const canConfirm = kind === "teachers" ? caps.teacherConfirm : caps.staffConfirm;
  const canManual = kind === "teachers" ? caps.teacherManual : caps.staffManual;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fallback = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kabul" });
      try {
        const { data } = await db.getAll(`attendance/admin/default-date?kind=${kind}`);
        if (cancelled) return;
        setTodayIsHoliday(Boolean(data?.summary?.todayIsHoliday));
        setDate(data?.summary?.date || fallback);
      } catch (_) {
        if (!cancelled) {
          setTodayIsHoliday(false);
          setDate(fallback);
        }
      } finally {
        if (!cancelled) setDateReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  const refresh = useCallback(async () => {
    if (!date) return false;
    const current = ++generation.current;
    setLoading(true); setDenied(false);
    try {
      const { data } = await db.getAll(`attendance/admin/${kind}?date=${date}&mode=${mode}&page=${page}&${extraFilters}`);
      if (!Array.isArray(data?.data)) throw new Error("پاسخ سرور معتبر نیست.");
      if (current === generation.current) setBody(data);
    } catch (err) {
      if (current === generation.current) {
        setDenied((err?.response?.status || err?.response?.data?.error?.statusCode) === 403);
        setMessage(errorMessage(err));
        setBody({ data: [], summary: {} });
      }
      return false;
    } finally { if (current === generation.current) setLoading(false); }
    return true;
  }, [kind, date, mode, page, search, adminState, headState]);
  useEffect(() => {
    if (!dateReady || !date) return;
    setEditor(null); setMessage(""); refresh();
    return () => { generation.current += 1; };
  }, [refresh, dateReady, date]);

  const mutate = async (row, operation, payload) => {
    if (busyRef.current || loading) return;
    const action = operation === "confirmation" ? payload.confirmation ? "تایید" : "رد"
      : operation === "absent" ? "غیر حاضر" : operation === "substitute" ? "تعیین استاد جایگزین" : "ثبت دستی (حاضر)";
    if (!await confirm(`${action}\n${row.person_name}\n${row.attendance_date}\n${row.class_desc || ""}\n${row.subject_name || ""}\n${operation === "substitute" ? options.find(o => o.teacher_id === Number(payload.teacherId))?.person_name || "" : ""}`)) return;
    busyRef.current = true; setBusy(true); setMessage("");
    try {
      const route = operation === "substitute" ? `lectures/${row.timetable}/substitute` : `${kind}/${row.id}/${operation}`;
      const { data } = await db.put(`attendance/admin/${route}`, payload);
      if (!data?.success || !Array.isArray(data.data) || Number(data.data[0]?.id) !== Number(row.id)) throw new Error("پاسخ سرور معتبر نیست. لیست را دوباره بارگیری کنید.");
      setBody(previous => ({ ...previous, data: previous.data.map(r => r.id === row.id ? data.data[0] : r) }));
      setEditor(null);
      const reloaded = await refresh();
      setMessage(reloaded ? "حاضری ثبت شد." : "ثبت شد؛ بارگیری مجدد ناموفق بود. دوباره بارگیری کنید.");
    } catch (err) { setMessage(errorMessage(err)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const openSubstitute = async (row) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setMessage("");
    try {
      const { data } = await db.getAll(`attendance/admin/lectures/${row.timetable}/substitutes?date=${row.attendance_date}`);
      setOptions(data?.data || []);
      setEditor({ row, operation: "substitute", teacherId: "" });
    } catch (err) { setMessage(errorMessage(err)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const openManual = (row) => {
    // Prefill from schedule (timetable for teachers, staff profile hours for workers).
    // Do not submit until the admin confirms.
    setEditor({
      row,
      operation: "manual",
      checkIn: clockTime(row.start_time) || clockTime(row.check_in_time),
      checkOut: clockTime(row.end_time) || clockTime(row.check_out_time),
      remarks: row.remarks || "",
    });
  };
  const change = (field, value) => setEditor(previous => ({ ...previous, [field]: value }));
  const attendanceActions = row => (
    <ActionGroup className="attendance-row-actions">
      {canConfirm ? (
        <>
          <button className="attendance-action is-approve" disabled={busy || loading} data-testid={`approve-${row.id}`} onClick={() => mutate(row, "confirmation", { confirmation: 1 })}>تایید</button>
          <button className="attendance-action is-deny" disabled={busy || loading} data-testid={`deny-${row.id}`} onClick={() => mutate(row, "confirmation", { confirmation: 0 })}>رد</button>
        </>
      ) : null}
      {canManual ? (
        <button className="attendance-action" disabled={busy || loading} data-testid={`manual-${row.id}`} onClick={() => openManual(row)}>ثبت دستی</button>
      ) : null}
      {kind === "teachers" && canManual ? (
        <button className="attendance-action is-deny" disabled={busy || loading} data-testid={`absent-${row.id}`} onClick={() => mutate(row, "absent", { state: 2 })}>غیر حاضر</button>
      ) : null}
      {kind === "teachers" && caps.substitute ? (
        <button className="attendance-action is-substitute" disabled={busy || loading} data-testid={`substitute-${row.id}`} onClick={() => openSubstitute(row)}>جایگزین</button>
      ) : null}
    </ActionGroup>
  );
  const columns = [
    { key: "record", label: "شماره", content: row => <span className="attendance-record-id">{row.id}</span> },
    { key: "person", label: kind === "teachers" ? "استاد" : "کارمند", content: row => <strong className="attendance-person-name">{row.person_name}</strong> },
    ...(kind === "teachers" ? [
      { path: "class_desc", label: "صنف" },
      { path: "subject_name", label: "مضمون" },
      { key: "sub", label: "نوع تدریس", content: row => Number(row.is_substitute) === 1 ? <StatusBadge tone="warning">جایگزین</StatusBadge> : <StatusBadge tone="neutral">اصلی</StatusBadge> },
    ] : []),
    { key: "state", label: "حاضری", content: row => <StatusBadge tone={stateTone(row.attendance_state)}>{stateLabel(row.attendance_state)}</StatusBadge> },
    { key: "checkIn", label: "ورود", content: row => <span dir="ltr">{clockTime(row.check_in_time) || "—"}</span> },
    { key: "checkOut", label: "خروج", content: row => <span dir="ltr">{clockTime(row.check_out_time) || "—"}</span> },
    { key: "firstConfirmation", label: kind === "teachers" ? "مدیر تدریسی" : "منابع بشری", content: row => {
      const value = kind === "teachers" ? row.confirmed_by_admin : row.confirmed_by_hr;
      return <StatusBadge tone={confirmationTone(value)}>{confirmationLabel(value)}</StatusBadge>;
    } },
    { key: "secondConfirmation", label: kind === "teachers" ? "رئیس پوهنحی" : "معاون اداری", content: row => {
      const value = kind === "teachers" ? row.confirmed_by_hod : row.confirmed_by_head;
      return <StatusBadge tone={confirmationTone(value)}>{confirmationLabel(value)}</StatusBadge>;
    } },
    { path: "remarks", label: "ملاحظات" },
    { key: "actions", label: "عملیات", content: attendanceActions },
  ];
  return (
    <div className="main-content attendance-admin mis-surface parity-list">
      {confirmationDialog}
      <div className="main-content-header">
        <div className="right-content">
          <MainTitle title="مدیریت حاضری" />
          <p className="attendance-page-subtitle">
            بررسی، تایید و اصلاح حاضری روزانه
          </p>
        </div>
        <div className="center-content" />
        <div className="left-content attendance-header-links">
          <DailyAttendanceReport kind={kind} date={date} disabled={denied || loading}/>
          <ParityReport
            icons
            print={false}
            title={kind==='teachers'?'گزارش حاضری استادان':'گزارش حاضری کارمندان'}
            subtitle={date}
            disabled={loading || denied}
            columns={[
            {label:'شماره',value:(_r,i)=>i+1},{path:'person_name',label:'اسم'},{path:'check_in_time',label:'زمان ورود'},{path:'check_out_time',label:'زمان خروج'},
            {label:kind==='teachers'?'تایید مدیر پوهنحی':'تایید مدیر منابع بشری',value:r=>confirmationLabel(kind==='teachers'?r.confirmed_by_admin:r.confirmed_by_hr)},
            {label:kind==='teachers'?'تایید ریس پوهنحی':'تایید معاون اداری',value:r=>confirmationLabel(kind==='teachers'?r.confirmed_by_hod:r.confirmed_by_head)},
            {label:'حاضری',value:r=>stateLabel(r.attendance_state)},{path:'remarks',label:'ملاحظات'}
          ]} loadRows={async()=>{const result=[];for(let p=1;;p++){const {data}=await db.getAll(`attendance/admin/${kind}?date=${date}&mode=${mode}&page=${p}&${extraFilters}`);result.push(...data.data);if(!data.summary?.hasMore)break;}return result;}}/>
        </div>
      </div>

      <SectionCard className="attendance-workflow-card">
        <div className="attendance-workflow-layout">
          <div>
            <span className="attendance-eyebrow">نوع گردش کار</span>
            <h2>
              {kind === "teachers" ? "حاضری استادان" : "حاضری کارمندان"}
            </h2>
            <p>
              {kind === "teachers"
                ? "تایید مدیر تدریسی و رئیس پوهنحی، ثبت دستی و تعیین استاد جایگزین"
                : "تایید منابع بشری و معاون اداری و ثبت دستی ساعات کاری"}
            </p>
          </div>
          <nav
            className="attendance-workflow-tabs"
            aria-label="گردش کار حاضری"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={kind === "teachers"}
              className={kind === "teachers" ? "active" : ""}
              data-testid="workflow-teachers"
              onClick={() => {
                setKind("teachers");
                setMode("all");
                setPage(1);
              }}
            >
              استادان
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={kind === "staff"}
              className={kind === "staff" ? "active" : ""}
              data-testid="workflow-staff"
              onClick={() => {
                setKind("staff");
                setMode("all");
                setPage(1);
              }}
            >
              کارمندان
            </button>
          </nav>
        </div>
      </SectionCard>

      <FilterBar
        className="attendance-filters"
        title="فیلتر حاضری"
        description="تاریخ و وضعیت تایید را انتخاب کنید."
        disabled={busy}
      >
        <label>
          تاریخ
          <input
            aria-label="Attendance date"
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>جستجو<input className="parity-search-input" aria-label="جستجوی حاضری" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/></label>
        {[[adminState,setAdminState,kind==='teachers'?'مدیر تدریسی':'مدیر منابع بشری'],[headState,setHeadState,kind==='teachers'?'ریس پوهنحی':'معاون اداری']].map(([value,setValue,label])=><label key={label}>{label}<select value={value} onChange={e=>{setValue(e.target.value);setPage(1);}}><option value="">عمومی</option><option value="pending">تایید ناشده</option><option value="confirmed">تایید شده</option><option value="denied">رد شده</option></select></label>)}
        {canConfirm ? (
          <label>
            وضعیت تایید
            <select
              aria-label="Confirmation filter"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">همه وضعیت‌ها</option>
              <option value="pending">در انتظار تایید</option>
              <option value="confirmed">تایید شده</option>
              <option value="denied">رد شده</option>
            </select>
          </label>
        ) : null}
        <div className="attendance-filter-summary">
          <span>موارد این صفحه</span>
          <strong>{body.data.length}</strong>
        </div>
        <button
          className="mis-button is-secondary"
          type="button"
          disabled={loading}
          onClick={() => {
            setMessage("");
            refresh();
          }}
        >
          بارگیری مجدد
        </button>
      </FilterBar>

      {message ? (
        <p className="attendance-message" role="status" aria-live="polite">
          {message}
        </p>
      ) : null}

      <SectionCard
        className="attendance-table-card"
        title={kind === "teachers" ? "لیست حاضری استادان" : "لیست حاضری کارمندان"}
        subtitle={`تاریخ انتخاب‌شده: ${date}`}
        actions={
          <StatusBadge tone={loading ? "warning" : "info"}>
            {loading ? "در حال بروزرسانی" : `${body.data.length} مورد`}
          </StatusBadge>
        }
      >
        {denied ? (
          <AccessDeniedResponse />
        ) : !dateReady || loading ? (
          <LoadingState />
        ) : body.data.length === 0 ? (
          <EmptyState
            title="موردی برای نمایش وجود ندارد"
            description={
              todayIsHoliday
                ? "امروز (پنجشنبه/جمعه یا رخصتی) روز درسی نیست. تاریخ آخرین روز کاری انتخاب شده است؛ در صورت نیاز تاریخ را تغییر دهید."
                : "در این تاریخ و وضعیت تایید، رکورد حاضری پیدا نشد. تاریخ یا فیلتر را تغییر دهید."
            }
          />
        ) : canConfirm ? (
          <div className="attendance-confirmation-grid" data-testid="attendance-confirmation-grid">
            {body.data.map(row => {
              const first = kind === "teachers" ? row.confirmed_by_admin : row.confirmed_by_hr;
              const second = kind === "teachers" ? row.confirmed_by_hod : row.confirmed_by_head;
              return (
                <article className="attendance-confirmation-card" key={row.id}>
                  <div className="attendance-confirmation-profile">
                    <RowPhoto src={kind === "teachers" ? row.teacher_photo : row.staff_photo} />
                    <span className={`attendance-confirmation-mark is-${confirmationTone(first)}`} title={confirmationLabel(first)}>1</span>
                    <span className={`attendance-confirmation-mark is-${confirmationTone(second)} is-second`} title={confirmationLabel(second)}>2</span>
                  </div>
                  <strong>{row.person_name}</strong>
                  <span>{kind === "teachers" ? row.class_desc : row.position_title}</span>
                  {kind === "teachers" && row.subject_name ? <small>{row.subject_name}</small> : null}
                  <div className="attendance-confirmation-times">
                    <span dir="ltr">ورود {clockTime(row.check_in_time) || "—"}</span>
                    <span dir="ltr">خروج {clockTime(row.check_out_time) || "—"}</span>
                  </div>
                  <StatusBadge tone={stateTone(row.attendance_state)}>{stateLabel(row.attendance_state)}</StatusBadge>
                  {row.remarks ? <small className="attendance-confirmation-remarks">{row.remarks}</small> : null}
                  {attendanceActions(row)}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="main-content-body attendance-table-wrap">
            <Table data={body.data} columns={columns} />
          </div>
        )}
      </SectionCard>

      <ActionGroup className="attendance-pagination" aria-label="صفحه‌بندی">
        <button
          className="mis-button is-secondary"
          disabled={busy || loading || page === 1}
          onClick={() => setPage((p) => p - 1)}
        >
          صفحه قبلی
        </button>
        <span>صفحه {page}</span>
        <button
          className="mis-button is-secondary"
          disabled={busy || loading || !body.summary?.hasMore}
          onClick={() => setPage((p) => p + 1)}
        >
          صفحه بعدی
        </button>
      </ActionGroup>

      {editor ? (
        <div
          className="attendance-dialog-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busy) setEditor(null);
          }}
        >
          <section
            className="attendance-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attendance-dialog-title"
            onKeyDown={(e) => {
              if (e.key === "Escape" && !busy) setEditor(null);
            }}
          >
            <header className="attendance-dialog-header">
              <div>
                <span className="attendance-eyebrow">
                  {editor.operation === "substitute"
                    ? "تعیین استاد جایگزین"
                    : "اصلاح حاضری"}
                </span>
                <h3 id="attendance-dialog-title">{editor.row.person_name}</h3>
              </div>
              <button
                className="attendance-dialog-close"
                type="button"
                aria-label="بستن"
                disabled={busy}
                onClick={() => setEditor(null)}
              >
                ×
              </button>
            </header>
            <dl className="attendance-dialog-context">
              <div>
                <dt>تاریخ</dt>
                <dd>{editor.row.attendance_date}</dd>
              </div>
              {editor.row.class_desc ? (
                <div>
                  <dt>صنف</dt>
                  <dd>{editor.row.class_desc}</dd>
                </div>
              ) : null}
              {editor.row.subject_name ? (
                <div>
                  <dt>مضمون</dt>
                  <dd>{editor.row.subject_name}</dd>
                </div>
              ) : null}
            </dl>
            {message ? <p className="attendance-message" role="alert">{message}</p> : null}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                mutate(
                  editor.row,
                  editor.operation,
                  editor.operation === "substitute"
                    ? {
                        date: editor.row.attendance_date,
                        teacherId: Number(editor.teacherId),
                      }
                    : {
                        date: editor.row.attendance_date,
                        checkIn:
                          editor.checkIn.length === 5
                            ? `${editor.checkIn}:00`
                            : editor.checkIn,
                        checkOut:
                          editor.checkOut.length === 5
                            ? `${editor.checkOut}:00`
                            : editor.checkOut,
                        remarks: editor.remarks,
                      }
                );
              }}
            >
              <fieldset disabled={busy}>
                {editor.operation === "substitute" ? (
                  <label className="mis-dialog-field">
                    استاد جایگزین
                    <select
                      autoFocus
                      required
                      aria-label="Substitute teacher"
                      value={editor.teacherId}
                      onChange={(e) => change("teacherId", e.target.value)}
                    >
                      <option value="">انتخاب استاد</option>
                      {options.map((option) => (
                        <option key={option.teacher_id} value={option.teacher_id}>
                          {option.person_name} ({option.teacher_id})
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <div className="attendance-dialog-fields">
                    <label className="mis-dialog-field">
                      ساعت ورود
                      <input
                        autoFocus
                        required
                        aria-label="Check in"
                        type="time"
                        step="1"
                        value={editor.checkIn}
                        onChange={(e) => change("checkIn", e.target.value)}
                      />
                    </label>
                    <label className="mis-dialog-field">
                      ساعت خروج
                      <input
                        required
                        aria-label="Check out"
                        type="time"
                        step="1"
                        value={editor.checkOut}
                        onChange={(e) => change("checkOut", e.target.value)}
                      />
                    </label>
                    <label className="mis-dialog-field is-full">
                      ملاحظات
                      <textarea
                        aria-label="Remarks"
                        maxLength="128"
                        value={editor.remarks}
                        onChange={(e) => change("remarks", e.target.value)}
                      />
                    </label>
                  </div>
                )}
                <ActionGroup className="attendance-dialog-actions">
                  <button
                    className="mis-button"
                    type="submit"
                    data-testid="save-attendance"
                  >
                    {busy ? "در حال ثبت…" : "تایید و ثبت"}
                  </button>
                  <button
                    className="mis-button is-secondary"
                    type="button"
                    onClick={() => setEditor(null)}
                  >
                    انصراف
                  </button>
                </ActionGroup>
              </fieldset>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
